import { lazy, Suspense } from "react";
// Route-level code splitting (2026-08-19 bundle diet): the admin +
// partner-admin trees, the appeal wizards, Shop and the giant EPForm are
// loaded on demand as separate chunks. The eager main bundle keeps only the
// tenant surfaces every session touches (login, dashboards, notice lists).
import { Dashboard } from "./pages/Dashboard";
import { Routes, Route, Navigate } from "react-router-dom";

import { Gstin } from "./pages/Gstin";
import { IncomeTax } from "./pages/IncomeTax";
import { Tds } from "./pages/Tds";
import { ComingSoon } from "./pages/ComingSoon";
import { Notice } from "./pages/Notice";
import { Clients } from "./pages/Clients";
import { ClientView } from "./pages/ClientView";
import { Eproceedings } from "./pages/income-tax/Eproceedings";
import ITTotalNoticesClients from "./pages/income-tax/ITTotalNoticesClients";

import { GSTLayout } from "./layouts/GSTLayout";
import { IncomeTaxLayout } from "./layouts/IncomeTaxLayout";
import { TDSLayout } from "./layouts/TDSLayout";
import { MainLayout } from "./layouts/MainLayout";
import Error404 from "./pages/Error404";
import { ServiceType } from "./utils/enums";
import { APPEAL_FEATURE_ENABLED } from "./utils/constants";
import { GSTNoticeDetails } from "./pages/gst/GSTNoticeDetails";
import { ResponseOutstandings } from "./pages/income-tax/RODemands";
import { GSTNoticeList } from "./pages/gst/GstNoticesList";
import GSTFetchTiming from "./pages/gst/GSTFetchTiming";
import { TDSNoticeList } from "./pages/tds/TDSNoticesList";
import { GSTAdditionalNoticesList } from "./pages/gst/GSTAdditionalNoticesList";
import AddEproceedingForm from "./components/AddEproceedingForm";
import AddResponseOutstandingsForm from "./components/AddResponseOutstandingsForm";
import AddGSTNoticeForm from "./components/AddGSTNoticeForm";

import AddAdditionalNoticeForm from "./components/AddAdditionalNoticeForm";
import Login from "./pages/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import OnboardingGuard from "./components/OnboardingGuard";
import ExpiredGuard from "./components/ExpiredGuard";
import EmbedRestrictedGuard from "./components/EmbedRestrictedGuard";
import ChoosePath from "./pages/onboarding/ChoosePath";

import AddonsPage from "./pages/shop/Addons";
import UsagePage from "./pages/Usage";
import Signup from "./pages/Signup";
import ResetPassword from "./components/ResetPassword";
import TermsAndConditions from "./pages/TermsAndConditions";
import ADDTDSNoticeForm from "./components/ADDTDSNoticeForm";
import Profile from "./pages/Profile";
import ReviewResponsePage from "./pages/review/ReviewResponsePage";
import ReviewDashboard from "./pages/review/ReviewDashboard";
import ReviewerProfile from "./pages/review/ReviewerProfile";
import ReviewerLayout from "./layouts/ReviewerLayout";
import ReviewRequestDetail from "./pages/review/ReviewRequestDetail";
import PublicChecklistUpload from "./pages/public/PublicChecklistUpload";
import AdminRoute from "./components/AdminRoute";
import AdminLayout from "./layouts/AdminLayout";
import TenantAdminRoute from "./components/TenantAdminRoute";
import TenantAdminEmbedRoute from "./components/TenantAdminEmbedRoute";
import TeamPage from "./pages/team/TeamPage";

import {
  GST_CLIENT_DETAIL,
  IT_CLIENT_DETAIL,
  TDS_CLIENT_DETAIL,
  GST_NOTICE_DETAIL,
  GST_ADDITIONAL_NOTICE_DETAIL,
  IT_EPROCEEDING_DETAIL,
  IT_ROD_DETAIL,
  TDS_NOTICE_DETAIL,
} from "./pages/admin/services/detailConfigs";
// AdminGstClients was the per-page R4 prototype; replaced by the
// generic AdminClientsPage in R5 (used for GST / IT / TDS clients).
import {
  GST_CLIENTS_COLUMNS,
  GST_NOTICES_COLUMNS,
  GST_ADDITIONAL_NOTICES_COLUMNS,
  IT_CLIENTS_COLUMNS,
  IT_EPROCEEDINGS_COLUMNS,
  IT_ROD_COLUMNS,
  TDS_CLIENTS_COLUMNS,
  // TDS_NOTICES_COLUMNS imported by AdminTdsNoticesPage directly.
} from "./pages/admin/services/columns";
import { ADMIN } from "./api/endpointMap";

import EmbedLayout, { EmbedNotFound } from "./layouts/EmbedLayout";
import EmbedNoticeDetail from "./pages/embed/EmbedNoticeDetail";
import EmbedGenerateResponse from "./pages/embed/EmbedGenerateResponse";

// eslint-disable-next-line no-unused-vars -- referenced from a
// commented-out route below; import retained so single-line revert.

import ImpersonationBanner from "./components/ImpersonationBanner";
import GstFetchResultPopup from "./components/GstFetchResultPopup";

// The tenant home is meaningless for org-less internal staff. Any
// navigation that lands a Review User on "/" (login fallbacks, guard
// redirects, a typed URL) bounces them to their own dashboard instead
// of showing locked portal cards.
const HomeForRole = ({ children }) => {
  let role = null;
  try {
    role = JSON.parse(localStorage.getItem("user") || "null")?.role || null;
  } catch {
    role = null;
  }
  if (role === "nabs_reviewer") {
    return <Navigate to="/review-dashboard" replace />;
  }
  return children;
};


// ---- Lazily-loaded route chunks (2026-08-19 bundle diet) ----
// Admin + partner-admin trees, appeal wizards, Shop, and the giant EPForm
// load on demand; the eager bundle keeps only everyday tenant surfaces.
const EPForm = lazy(() =>
  import("./pages/income-tax/EPForm").then((m) => ({ default: m.EPForm }))
);
const AppealsList = lazy(() => import("./pages/gst/appeals/AppealsList"));
const AppealWizard = lazy(() => import("./pages/gst/appeals/AppealWizard"));
const ManualOrderForm = lazy(() => import("./pages/gst/appeals/ManualOrderForm"));
const ITAppealWizard = lazy(() => import("./pages/income-tax/appeals/ITAppealWizard"));
const Shop = lazy(() => import("./pages/shop/Shop"));
const AdminOrganizations = lazy(() => import("./pages/admin/AdminOrganizations"));
const AdminOrganizationDetail = lazy(() => import("./pages/admin/AdminOrganizationDetail"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminPartners = lazy(() => import("./pages/admin/partners/AdminPartners"));
const AdminPartnerDetail = lazy(() => import("./pages/admin/partners/AdminPartnerDetail"));
const AdminPartnerCreate = lazy(() => import("./pages/admin/partners/AdminPartnerCreate"));
const AdminPartnerEdit = lazy(() => import("./pages/admin/partners/AdminPartnerEdit"));
const AdminRevenue = lazy(() => import("./pages/admin/dashboards/AdminRevenue"));
const AdminAIUsage = lazy(() => import("./pages/admin/dashboards/AdminAIUsage"));
const AdminOpsHealth = lazy(() => import("./pages/admin/dashboards/AdminOpsHealth"));
const AdminBillingDashboard = lazy(() => import("./pages/admin/dashboards/AdminBillingDashboard"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminPlanCreate = lazy(() => import("./pages/admin/plans/AdminPlanCreate"));
const AdminPlanEdit = lazy(() => import("./pages/admin/plans/AdminPlanEdit"));
const AdminPlanCategories = lazy(() => import("./pages/admin/AdminPlanCategories"));
const AdminPlanFeatures = lazy(() => import("./pages/admin/AdminPlanFeatures"));
const AdminTrialConfig = lazy(() => import("./pages/admin/AdminTrialConfig"));
const AdminCaseBankConfig = lazy(() => import("./pages/admin/AdminCaseBankConfig"));
const AdminCaseBankDocs = lazy(() => import("./pages/admin/AdminCaseBankDocs"));
const AdminReviewUsers = lazy(() => import("./pages/admin/AdminReviewUsers"));
const AdminReviewSettings = lazy(() => import("./pages/admin/AdminReviewSettings"));
// Per-portal request tables for the reviewer workspace (the dashboard itself
// is analytics-only since 05-10-2026).
const ReviewPortalRequests = lazy(() => import("./pages/review/ReviewPortalRequests"));
const ReviewNoticeView = lazy(() => import("./pages/review/ReviewNoticeView"));
const AdminCaseBankDocDetail = lazy(() => import("./pages/admin/AdminCaseBankDocDetail"));
const AdminCeleryWorkers = lazy(() => import("./pages/admin/AdminCeleryWorkers"));
const AdminCeleryQueue = lazy(() => import("./pages/admin/AdminCeleryQueue"));
const AdminSystemConfig = lazy(() => import("./pages/admin/AdminSystemConfig"));
const AdminWhatsAppTemplates = lazy(() => import("./pages/admin/AdminWhatsAppTemplates"));
const AdminWhatsAppTemplateForm = lazy(() => import("./pages/admin/AdminWhatsAppTemplateForm"));
const AdminDatabaseBackups = lazy(() => import("./pages/admin/AdminDatabaseBackups"));
const AdminWhitebooksConfig = lazy(() => import("./pages/admin/AdminWhitebooksConfig"));
const AdminCostConfig = lazy(() => import("./pages/admin/AdminCostConfig"));
const AdminUsage = lazy(() => import("./pages/admin/AdminUsage"));
const AdminLambdaAnalytics = lazy(() => import("./pages/admin/AdminLambdaAnalytics"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminBulkOps = lazy(() => import("./pages/admin/AdminBulkOps"));
const AdminDeletedOrganizations = lazy(() => import("./pages/admin/AdminDeletedOrganizations"));
const AdminPartnerAdminUsers = lazy(() => import("./pages/admin/AdminPartnerAdminUsers"));
const AdminSandboxAccessRequests = lazy(() => import("./pages/admin/partners/AdminSandboxAccessRequests"));
const PortalClient = lazy(() => import("./pages/admin/PortalClient"));
const ParallelFetchLogs = lazy(() => import("./pages/admin/ParallelFetchLogs"));
const ParallelFetchLogDetail = lazy(() => import("./pages/admin/ParallelFetchLogDetail"));
const CostEventLogs = lazy(() => import("./pages/admin/CostEventLogs"));
const PartnerBillingLogs = lazy(() => import("./pages/admin/PartnerBillingLogs"));
const PartnerBillingLogDetail = lazy(() => import("./pages/admin/PartnerBillingLogDetail"));
const CostEventLogDetail = lazy(() => import("./pages/admin/CostEventLogDetail"));
const Otps = lazy(() => import("./pages/admin/Otps"));
const OtpDetail = lazy(() => import("./pages/admin/OtpDetail"));
const AdminGstAuthSessions = lazy(() => import("./pages/admin/AdminGstAuthSessions"));
const AdminGstAuthSessionDetail = lazy(() => import("./pages/admin/AdminGstAuthSessionDetail"));
const EmailLogs = lazy(() => import("./pages/admin/EmailLogs"));
const EmailLogDetail = lazy(() => import("./pages/admin/EmailLogDetail"));
const GenerateResponseLogs = lazy(() => import("./pages/admin/GenerateResponseLogs"));
const GenerateResponseLogDetail = lazy(() => import("./pages/admin/GenerateResponseLogDetail"));
const ComprehensiveAnalysisQueryLogs = lazy(() => import("./pages/admin/ComprehensiveAnalysisQueryLogs"));
const ComprehensiveAnalysisQueryLogDetail = lazy(() => import("./pages/admin/ComprehensiveAnalysisQueryLogDetail"));
const ImportLogs = lazy(() => import("./pages/admin/ImportLogs"));
const ImportLogDetail = lazy(() => import("./pages/admin/ImportLogDetail"));
const LoginFailedLogs = lazy(() => import("./pages/admin/LoginFailedLogs"));
const LoginFailedLogDetail = lazy(() => import("./pages/admin/LoginFailedLogDetail"));
const AdminServicePagePlaceholder = lazy(() => import("./pages/admin/services/AdminServicePagePlaceholder"));
const AdminClientsPage = lazy(() => import("./pages/admin/services/AdminClientsPage"));
const AdminNoticesPage = lazy(() => import("./pages/admin/services/AdminNoticesPage"));
const AdminItNoticesPage = lazy(() => import("./pages/admin/services/AdminItNoticesPage"));
const AdminTdsNoticesPage = lazy(() => import("./pages/admin/services/AdminTdsNoticesPage"));
const AdminRecordDetailPage = lazy(() => import("./pages/admin/services/AdminRecordDetailPage"));
const AdminNoticeReadOnlyForm = lazy(() => import("./pages/admin/services/AdminNoticeReadOnlyForm"));
const AdminNoticeSummaryView = lazy(() => import("./pages/admin/services/AdminNoticeSummaryView"));


function App() {
  return (
    <>
      <ImpersonationBanner />
      <GstFetchResultPopup />
      <Suspense
        fallback={
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", color: "#64748b" }}>
            Loading...
          </div>
        }
      >
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

        {/* Post-signup onboarding choice page — authenticated but
            outside the regular MainLayout chrome so first-time users
            land on a full-bleed marketing-style page. The OnboardingGuard
            on every other protected route bounces here while
            has_chosen_subscription_path is false. */}
        <Route
          path="/onboarding/choose-path"
          element={
            <ProtectedRoute>
              <ChoosePath />
            </ProtectedRoute>
          }
        />

        {/* Shop / Upgrade page (Razorpay checkout + plan catalogue).
            Exempt from OnboardingGuard so users who pick "Purchase a
            Plan" on the choose-path page can land here even while
            has_chosen_subscription_path is still false. Wrapped in
            MainLayout so the regular navbar (trial countdown,
            Upgrade, Usage, Logout) appears consistently. */}
        {/* Usage page — accessible to every logged-in user (admin + regular).
            Reachable from the navbar 'Usage' link. Trial-expired users
            stay reachable too via the EXPIRED_EXEMPT_PREFIXES list, so
            they can see what they have consumed before paying — that
            framing strengthens the Shop CTA on the same page.
            Wrapped in the same MainLayout group as /shop so the
            navbar (Free Trial pill / Upgrade / Usage / Logout) renders
            consistently across both billing surfaces. */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Upgrade (Shop) + Usage are self-serve billing surfaces that
              do not apply to partner-SSO (embedded) users — their plan is
              owned by the partner. EmbedRestrictedGuard bounces such users
              to the embed dashboard if they reach these by direct URL; the
              navbar buttons are already hidden in embed mode. */}
          <Route
            path="/shop"
            element={
              <EmbedRestrictedGuard>
                <Shop />
              </EmbedRestrictedGuard>
            }
          />
          <Route
            path="/shop/addons"
            element={
              <EmbedRestrictedGuard>
                <AddonsPage />
              </EmbedRestrictedGuard>
            }
          />
          <Route
            path="/usage"
            element={
              <EmbedRestrictedGuard>
                <UsagePage />
              </EmbedRestrictedGuard>
            }
          />
        </Route>

        <Route
          path="/"
          index
          element={
            <HomeForRole>
              <ProtectedRoute>
                <OnboardingGuard>
                  <ExpiredGuard>
                    <Dashboard />
                  </ExpiredGuard>
                </OnboardingGuard>
              </ProtectedRoute>
            </HomeForRole>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <ExpiredGuard>
                  <MainLayout />
                </ExpiredGuard>
              </OnboardingGuard>
            </ProtectedRoute>
          }
        >
          <Route path="/coming-soon" element={<ComingSoon />} />
          <Route path="/my-account-profile-mv" element={<Profile />} />
          {/* Review Response (Review Mode). Its own page rather than a
              dialog: the reviewer needs the full width for the response
              and its sources side by side. Portal-agnostic, so it sits
              here instead of being repeated under each portal. */}
          <Route
            path="/response-review/:sessionId"
            element={<ReviewResponsePage />}
          />
        </Route>

        {/* Reviewer workspace — the Review User's (nabs_reviewer) surface,
            with its own sidebar + header chrome (ReviewerLayout). Outside
            the tenant layout: a reviewer has no organisation, so the
            tenant Navbar's org-scoped widgets would all be empty/broken
            for them. */}
        <Route
          element={
            <ProtectedRoute>
              <ReviewerLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/review-dashboard" element={<ReviewDashboard />} />
          {/* Per-portal request tables (the dashboard itself is analytics). */}
          <Route
            path="/review-dashboard/income-tax"
            element={<ReviewPortalRequests portal="income_tax" />}
          />
          <Route
            path="/review-dashboard/gst"
            element={<ReviewPortalRequests portal="gst" />}
          />
          <Route
            path="/review-dashboard/tds"
            element={<ReviewPortalRequests portal="tds" />}
          />
          <Route path="/reviewer-profile" element={<ReviewerProfile />} />
          <Route
            path="/review-requests/:requestId"
            element={<ReviewRequestDetail />}
          />
          {/* The notice behind a request, full page (05-10-2026). */}
          <Route
            path="/review-requests/:requestId/notice"
            element={<ReviewNoticeView />}
          />
        </Route>

        {/* Public no-login checklist upload page — opened by a direct
            subscriber from the WhatsApp/Email link a reviewer shared.
            Token-authorised on the BE; deliberately outside every auth
            wrapper and layout. */}
        <Route
          path="/checklist-upload/:token"
          element={<PublicChecklistUpload />}
        />

        {/* Team management (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.5) — admin-
            only. <TenantAdminRoute /> gates on tenant_admin / super_admin;
            <MainLayout /> reuses the regular tenant Navbar/Footer chrome
            so the page sits in the same visual context as Profile. */}
        <Route element={<TenantAdminRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/team" element={<TeamPage />} />
          </Route>
        </Route>

        <Route
          path={`/${ServiceType.GSTIN}`}
          element={
            <ProtectedRoute portalName="GST">
              <ExpiredGuard>
                <GSTLayout />
              </ExpiredGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<Gstin serviceType={ServiceType.GSTIN} />} />

          <Route
            path="clients"
            element={<Clients serviceType={ServiceType.GSTIN} />}
          />
          <Route
            path="client-view/:clientId"
            element={<ClientView serviceType={ServiceType.GSTIN} />}
          />
          <Route
            path="clients/:clientId"
            element={<ClientView serviceType={ServiceType.GSTIN} />}
          />
          <Route
            path="eproceedings/:clientId"
            element={<Eproceedings serviceType={ServiceType.GSTIN} />}
          />
          <Route
            path="eproceeding-details"
            element={<EPForm serviceType={ServiceType.GSTIN} />}
          />
          <Route
            path="failed-login-details"
            element={<EPForm serviceType={ServiceType.GSTIN} />}
          />
          <Route
            path="notices"
            element={<GSTNoticeList serviceType={ServiceType.GSTIN} />}
          />

          <Route
            path="notices/:clientId"
            element={<GSTNoticeList serviceType={ServiceType.GSTIN} />}
          />

          <Route path="fetch-timing/:logId" element={<GSTFetchTiming />} />

          {/* GST Appeal routes — omitted when the Appeal feature is globally
              disabled (production hold), so direct URLs fall through to 404. */}
          {APPEAL_FEATURE_ENABLED && (
            <>
              <Route path="appeals" element={<AppealsList />} />
              <Route path="appeals/draft/:caseDetailsId" element={<AppealWizard />} />
              <Route path="appeals/manual-order" element={<ManualOrderForm />} />
            </>
          )}

          <Route
            path="add-notices/:clientId"
            element={<AddGSTNoticeForm serviceType={ServiceType.GSTIN} />}
          />
          <Route
            path="additional-notices/:clientId"
            element={
              <GSTAdditionalNoticesList serviceType={ServiceType.GSTIN} />
            }
          />
          <Route
            path="add-additional-notices/:clientId"
            element={
              <AddAdditionalNoticeForm serviceType={ServiceType.GSTIN} />
            }
          />
          <Route
            path="notice-details"
            element={<EPForm serviceType={ServiceType.GSTIN} />}
          />
        </Route>

        <Route
          path={`/${ServiceType.INCOME_TAX}`}
          element={
            <ProtectedRoute portalName={"Income Tax"}>
              <ExpiredGuard>
                <IncomeTaxLayout />
              </ExpiredGuard>
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={<IncomeTax serviceType={ServiceType.INCOME_TAX} />}
          />
          <Route
            path="notice"
            element={<Notice serviceType={ServiceType.INCOME_TAX} />}
          />
          <Route
            path="clients"
            element={<Clients serviceType={ServiceType.INCOME_TAX} />}
          />
          <Route
            path="client-view/:clientId"
            element={<ClientView serviceType={ServiceType.INCOME_TAX} />}
          />
          <Route
            path="eproceedings/:clientId"
            element={<Eproceedings serviceType={ServiceType.INCOME_TAX} />}
          />
          <Route
            path="notices"
            element={<Eproceedings serviceType={ServiceType.INCOME_TAX} />}
          />
          <Route
            path="total-notices-clients"
            element={<ITTotalNoticesClients />}
          />

          <Route
            path="add-eproceedings/:clientId"
            element={
              <AddEproceedingForm serviceType={ServiceType.INCOME_TAX} />
            }
          />
          <Route
            path="eproceeding-details"
            element={<EPForm serviceType={ServiceType.INCOME_TAX} />}
          />
          {/* IT Appeal route — omitted when the Appeal feature is globally
              disabled (production hold). */}
          {APPEAL_FEATURE_ENABLED && (
            <Route
              path="appeals/draft/:eProceedingId"
              element={<ITAppealWizard />}
            />
          )}
          <Route
            path="failed-login-details"
            element={<EPForm serviceType={ServiceType.INCOME_TAX} />}
          />
          <Route
            path="responseoutstandings/:clientId"
            element={
              <ResponseOutstandings serviceType={ServiceType.INCOME_TAX} />
            }
          />

          <Route
            path="add-responseoutstandings/:clientId"
            element={
              <AddResponseOutstandingsForm
                serviceType={ServiceType.INCOME_TAX}
              />
            }
          />

          <Route
            path="responseoutstandings-details"
            element={<EPForm serviceType={ServiceType.INCOME_TAX} />}
          />
        </Route>

        <Route
          path={`/${ServiceType.TDS}`}
          element={
            <ProtectedRoute portalName={"TDS"}>
              <ExpiredGuard>
                <TDSLayout />
              </ExpiredGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<Tds serviceType={ServiceType.TDS} />} />

          <Route
            path="clients"
            element={<Clients serviceType={ServiceType.TDS} />}
          />
          <Route
            path="client-view/:clientId"
            element={<ClientView serviceType={ServiceType.TDS} />}
          />
          <Route
            path="eproceedings/:clientId"
            element={<Eproceedings serviceType={ServiceType.TDS} />}
          />
          <Route
            path="eproceeding-details"
            element={<EPForm serviceType={ServiceType.TDS} />}
          />
          <Route
            path="notices"
            element={<TDSNoticeList serviceType={ServiceType.TDS} />}
          />

          <Route
            path="notices/:clientId"
            element={<TDSNoticeList serviceType={ServiceType.TDS} />}
          />
          <Route
            path="add-notices/:clientId"
            element={<ADDTDSNoticeForm serviceType={ServiceType.TDS} />}
          />
          <Route
            path="notice-details"
            element={<EPForm serviceType={ServiceType.TDS} />}
          />
          <Route
            path="failed-login-details"
            element={<EPForm serviceType={ServiceType.TDS} />}
          />
        </Route>

        {/* Admin Portal Routes (super_admin only) */}
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="notice-ai/organizations" replace />} />

            {/* Notice AI scope — direct customers (partner_id IS NULL).
                AdminScopeContext derives scope="direct" from this prefix. */}
            <Route path="notice-ai">
              <Route index element={<Navigate to="organizations" replace />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              <Route path="organizations/:orgId" element={<AdminOrganizationDetail />} />
              <Route path="deleted-organizations" element={<AdminDeletedOrganizations />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="revenue" element={<AdminRevenue />} />
              <Route path="ai-usage" element={<AdminAIUsage />} />
              <Route path="ops-health" element={<AdminOpsHealth />} />
              {/* R17 — Portal Client dashboard + 8 scoped list pages.
                  Components are R3 reuse; scope is auto-derived from
                  the /admin/notice-ai/* URL prefix and sent to the BE
                  as ?scope=direct so Organization.partner_id IS NULL
                  rows are returned. */}
              <Route path="portal-client" element={<PortalClient />} />
              <Route path="gst/clients" element={
                <AdminClientsPage
                  title="GST Clients"
                  description="GST clients (direct customers)."
                  telemetryKey="admin.notice-ai.gst.clients"
                  listEndpoint={ADMIN.SERVICE_GST_CLIENTS}
                  partnerRefsEndpoint={ADMIN.SERVICE_GST_CLIENT_PARTNER_REFS}
                  assigneesEndpoint={ADMIN.SERVICE_GST_CLIENT_ASSIGNEES}
                  deletePath="/api/v1/admin/gst/clients"
                  detailBasePath="/admin/gst/clients"
                  columns={GST_CLIENTS_COLUMNS}
                  searchPlaceholder="Search by client name, GSTIN, or GST username"
                  emptyText="No GST clients match your filters."
                  xScroll={1600}
                />
              } />
              <Route path="gst/notices" element={
                <AdminNoticesPage
                  title="GST Notices & Orders"
                  description="GST notices and orders (direct customers)."
                  telemetryKey="admin.notice-ai.gst.notices"
                  listEndpoint={ADMIN.SERVICE_GST_NOTICES}
                  clientNamesEndpoint={ADMIN.SERVICE_GST_NOTICES_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_GST_NOTICES_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_GST_NOTICES_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/gst/notices"
                  detailBasePath="/admin/gst/notices"
                  columns={GST_NOTICES_COLUMNS}
                  emptyText="No GST notices match your filters."
                  xScroll={1500}
                />
              } />
              <Route path="gst/additional-notices" element={
                <AdminNoticesPage
                  title="GST Additional Notices"
                  description="GST additional notices (direct customers)."
                  telemetryKey="admin.notice-ai.gst.additional-notices"
                  listEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES}
                  clientNamesEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/gst/additional-notices"
                  detailBasePath="/admin/gst/additional-notices"
                  columns={GST_ADDITIONAL_NOTICES_COLUMNS}
                  emptyText="No GST additional notices match your filters."
                  xScroll={1500}
                />
              } />
              <Route path="income-tax/clients" element={
                <AdminClientsPage
                  title="Income Tax Clients"
                  description="Income Tax clients (direct customers)."
                  telemetryKey="admin.notice-ai.income-tax.clients"
                  listEndpoint={ADMIN.SERVICE_IT_CLIENTS}
                  partnerRefsEndpoint={ADMIN.SERVICE_IT_CLIENT_PARTNER_REFS}
                  assigneesEndpoint={ADMIN.SERVICE_IT_CLIENT_ASSIGNEES}
                  deletePath="/api/v1/admin/income-tax/clients"
                  detailBasePath="/admin/income-tax/clients"
                  columns={IT_CLIENTS_COLUMNS}
                  searchPlaceholder="Search by client name or PAN/TAN"
                  emptyText="No Income Tax clients match your filters."
                  xScroll={1600}
                />
              } />
              <Route path="income-tax/eproceedings" element={
                <AdminItNoticesPage
                  title="Income Tax E-Proceedings"
                  description="E-Proceedings (direct customers)."
                  telemetryKey="admin.notice-ai.income-tax.eproceedings"
                  listEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS}
                  clientNamesEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/income-tax/eproceedings"
                  detailBasePath="/admin/income-tax/eproceedings"
                  columns={IT_EPROCEEDINGS_COLUMNS}
                  emptyText="No E-Proceedings match your filters."
                  xScroll={1500}
                />
              } />
              <Route path="income-tax/response-outstanding-demands" element={
                <AdminItNoticesPage
                  title="Income Tax - Response to Outstanding Demands"
                  description="ROD (direct customers)."
                  telemetryKey="admin.notice-ai.income-tax.response-outstanding-demands"
                  listEndpoint={ADMIN.SERVICE_IT_ROD}
                  clientNamesEndpoint={ADMIN.SERVICE_IT_ROD_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_IT_ROD_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_IT_ROD_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/income-tax/response-outstanding-demands"
                  detailBasePath="/admin/income-tax/response-outstanding-demands"
                  columns={IT_ROD_COLUMNS}
                  emptyText="No outstanding-demand responses match your filters."
                  xScroll={1500}
                />
              } />
              <Route path="tds-tcs/clients" element={
                <AdminClientsPage
                  title="TDS / TCS Clients"
                  description="TDS / TCS clients (direct customers)."
                  telemetryKey="admin.notice-ai.tds-tcs.clients"
                  listEndpoint={ADMIN.SERVICE_TDS_CLIENTS}
                  partnerRefsEndpoint={ADMIN.SERVICE_TDS_CLIENT_PARTNER_REFS}
                  assigneesEndpoint={ADMIN.SERVICE_TDS_CLIENT_ASSIGNEES}
                  deletePath="/api/v1/admin/tds-tcs/clients"
                  detailBasePath="/admin/tds-tcs/clients"
                  columns={TDS_CLIENTS_COLUMNS}
                  searchPlaceholder="Search by client name, TAN, or TDS username"
                  emptyText="No TDS / TCS clients match your filters."
                  xScroll={1700}
                />
              } />
              <Route path="tds-tcs/notices" element={
                <AdminTdsNoticesPage
                  deletePath="/api/v1/admin/tds-tcs/notices"
                  detailBasePath="/admin/tds-tcs/notices"
                />
              } />
            </Route>

            {/* Partner Admin scope — partner-mediated tenants
                (partner_id IS NOT NULL). scope="partner". */}
            <Route path="partner">
              <Route index element={<Navigate to="organizations" replace />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              <Route path="organizations/:orgId" element={<AdminOrganizationDetail />} />
              <Route path="deleted-organizations" element={<AdminDeletedOrganizations />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="revenue" element={<AdminRevenue />} />
              <Route path="ai-usage" element={<AdminAIUsage />} />
              <Route path="ops-health" element={<AdminOpsHealth />} />
              {/* R17 — Portal Client + 8 scoped list pages, scope=partner. */}
              <Route path="portal-client" element={<PortalClient />} />
              <Route path="gst/clients" element={
                <AdminClientsPage
                  title="GST Clients"
                  description="GST clients (partner-mediated)."
                  telemetryKey="admin.partner.gst.clients"
                  listEndpoint={ADMIN.SERVICE_GST_CLIENTS}
                  partnerRefsEndpoint={ADMIN.SERVICE_GST_CLIENT_PARTNER_REFS}
                  assigneesEndpoint={ADMIN.SERVICE_GST_CLIENT_ASSIGNEES}
                  deletePath="/api/v1/admin/gst/clients"
                  detailBasePath="/admin/gst/clients"
                  columns={GST_CLIENTS_COLUMNS}
                  searchPlaceholder="Search by client name, GSTIN, or GST username"
                  emptyText="No GST clients match your filters."
                  xScroll={1600}
                />
              } />
              <Route path="gst/notices" element={
                <AdminNoticesPage
                  title="GST Notices & Orders"
                  description="GST notices and orders (partner-mediated)."
                  telemetryKey="admin.partner.gst.notices"
                  listEndpoint={ADMIN.SERVICE_GST_NOTICES}
                  clientNamesEndpoint={ADMIN.SERVICE_GST_NOTICES_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_GST_NOTICES_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_GST_NOTICES_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/gst/notices"
                  detailBasePath="/admin/gst/notices"
                  columns={GST_NOTICES_COLUMNS}
                  emptyText="No GST notices match your filters."
                  xScroll={1500}
                />
              } />
              <Route path="gst/additional-notices" element={
                <AdminNoticesPage
                  title="GST Additional Notices"
                  description="GST additional notices (partner-mediated)."
                  telemetryKey="admin.partner.gst.additional-notices"
                  listEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES}
                  clientNamesEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/gst/additional-notices"
                  detailBasePath="/admin/gst/additional-notices"
                  columns={GST_ADDITIONAL_NOTICES_COLUMNS}
                  emptyText="No GST additional notices match your filters."
                  xScroll={1500}
                />
              } />
              <Route path="income-tax/clients" element={
                <AdminClientsPage
                  title="Income Tax Clients"
                  description="Income Tax clients (partner-mediated)."
                  telemetryKey="admin.partner.income-tax.clients"
                  listEndpoint={ADMIN.SERVICE_IT_CLIENTS}
                  partnerRefsEndpoint={ADMIN.SERVICE_IT_CLIENT_PARTNER_REFS}
                  assigneesEndpoint={ADMIN.SERVICE_IT_CLIENT_ASSIGNEES}
                  deletePath="/api/v1/admin/income-tax/clients"
                  detailBasePath="/admin/income-tax/clients"
                  columns={IT_CLIENTS_COLUMNS}
                  searchPlaceholder="Search by client name or PAN/TAN"
                  emptyText="No Income Tax clients match your filters."
                  xScroll={1600}
                />
              } />
              <Route path="income-tax/eproceedings" element={
                <AdminItNoticesPage
                  title="Income Tax E-Proceedings"
                  description="E-Proceedings (partner-mediated)."
                  telemetryKey="admin.partner.income-tax.eproceedings"
                  listEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS}
                  clientNamesEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/income-tax/eproceedings"
                  detailBasePath="/admin/income-tax/eproceedings"
                  columns={IT_EPROCEEDINGS_COLUMNS}
                  emptyText="No E-Proceedings match your filters."
                  xScroll={1500}
                />
              } />
              <Route path="income-tax/response-outstanding-demands" element={
                <AdminItNoticesPage
                  title="Income Tax - Response to Outstanding Demands"
                  description="ROD (partner-mediated)."
                  telemetryKey="admin.partner.income-tax.response-outstanding-demands"
                  listEndpoint={ADMIN.SERVICE_IT_ROD}
                  clientNamesEndpoint={ADMIN.SERVICE_IT_ROD_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_IT_ROD_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_IT_ROD_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/income-tax/response-outstanding-demands"
                  detailBasePath="/admin/income-tax/response-outstanding-demands"
                  columns={IT_ROD_COLUMNS}
                  emptyText="No outstanding-demand responses match your filters."
                  xScroll={1500}
                />
              } />
              <Route path="tds-tcs/clients" element={
                <AdminClientsPage
                  title="TDS / TCS Clients"
                  description="TDS / TCS clients (partner-mediated)."
                  telemetryKey="admin.partner.tds-tcs.clients"
                  listEndpoint={ADMIN.SERVICE_TDS_CLIENTS}
                  partnerRefsEndpoint={ADMIN.SERVICE_TDS_CLIENT_PARTNER_REFS}
                  assigneesEndpoint={ADMIN.SERVICE_TDS_CLIENT_ASSIGNEES}
                  deletePath="/api/v1/admin/tds-tcs/clients"
                  detailBasePath="/admin/tds-tcs/clients"
                  columns={TDS_CLIENTS_COLUMNS}
                  searchPlaceholder="Search by client name, TAN, or TDS username"
                  emptyText="No TDS / TCS clients match your filters."
                  xScroll={1700}
                />
              } />
              <Route path="tds-tcs/notices" element={
                <AdminTdsNoticesPage
                  deletePath="/api/v1/admin/tds-tcs/notices"
                  detailBasePath="/admin/tds-tcs/notices"
                />
              } />
            </Route>

            {/* Legacy / global routes — scope="all". Kept for backwards
                compatibility (direct URL access during transition) and for
                pages that aren't tenant-scoped (Partners, Plans, etc.). */}
            <Route path="organizations" element={<AdminOrganizations />} />
            <Route
              path="organizations/:orgId"
              element={<AdminOrganizationDetail />}
            />
            <Route
              path="deleted-organizations"
              element={<AdminDeletedOrganizations />}
            />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:userId" element={<AdminUserDetail />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="partners/new" element={<AdminPartnerCreate />} />
            <Route
              path="partners/:partnerId/edit"
              element={<AdminPartnerEdit />}
            />
            <Route
              path="partners/:partnerId"
              element={<AdminPartnerDetail />}
            />
            <Route
              path="partner-admin-users"
              element={<AdminPartnerAdminUsers />}
            />
            {/* Review queue for the public /request-access form on the
                partner portal. Approving here is what creates the partner. */}
            <Route
              path="sandbox-access-requests"
              element={<AdminSandboxAccessRequests />}
            />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="ai-usage" element={<AdminAIUsage />} />
            <Route path="ops-health" element={<AdminOpsHealth />} />
            {/* Platform-wide Usage — same shape as the (now hidden)
                Partner Admin Usage page but cross-tenant. Backed by
                /admin/usage + /admin/usage/timeseries. */}
            <Route path="usage" element={<AdminUsage />} />
            {/* Lambda usage deep-dive. Linked from the Reliability
                section of /admin/usage via a "View More" affordance. */}
            <Route path="lambda-analytics" element={<AdminLambdaAnalytics />} />
            {/* USAGE_TRACKING_PLAN Phase B — platform-wide billing rollup
                (no scope filter, sums across all orgs/partners). */}
            <Route path="billing" element={<AdminBillingDashboard />} />
            {/* Slice 4 — cross-tenant Payment History / Orders. */}
            <Route path="payments" element={<AdminPayments />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="plans/new" element={<AdminPlanCreate />} />
            <Route
              path="plans/:planCode/edit"
              element={<AdminPlanEdit />}
            />
            <Route
              path="plan-categories"
              element={<AdminPlanCategories />}
            />
            <Route
              path="plan-features"
              element={<AdminPlanFeatures />}
            />
            <Route
              path="trial-config"
              element={<AdminTrialConfig />}
            />
            <Route
              path="review-users"
              element={<AdminReviewUsers />}
            />
            <Route
              path="review-settings"
              element={<AdminReviewSettings />}
            />
            <Route
              path="case-bank/settings"
              element={<AdminCaseBankConfig />}
            />
            <Route
              path="case-bank"
              element={<AdminCaseBankDocs />}
            />
            <Route
              path="case-bank/:tid"
              element={<AdminCaseBankDocDetail />}
            />
            <Route
              path="system-config"
              element={<AdminSystemConfig />}
            />
            <Route
              path="whatsapp-templates"
              element={<AdminWhatsAppTemplates />}
            />
            <Route
              path="whatsapp-templates/new"
              element={<AdminWhatsAppTemplateForm />}
            />
            <Route
              path="whatsapp-templates/:templateId"
              element={<AdminWhatsAppTemplateForm />}
            />
            <Route
              path="celery-workers"
              element={<AdminCeleryWorkers />}
            />
            <Route
              path="celery-queue"
              element={<AdminCeleryQueue />}
            />
            <Route
              path="database-backups"
              element={<AdminDatabaseBackups />}
            />
            <Route
              path="whitebooks-config"
              element={<AdminWhitebooksConfig />}
            />
            <Route
              path="cost-config"
              element={<AdminCostConfig />}
            />
            <Route path="bulk-ops" element={<AdminBulkOps />} />
            {/* R19 — Parallel Fetch Log admin surface. Global (not
                scoped), super_admin only. */}
            <Route
              path="parallel-fetch-logs"
              element={<ParallelFetchLogs />}
            />
            {/* R20 — click-through detail for one parallel fetch log. */}
            <Route
              path="parallel-fetch-logs/:logId"
              element={<ParallelFetchLogDetail />}
            />
            {/* R22 — Cost Event Log admin surface + detail. */}
            <Route
              path="cost-event-logs"
              element={<CostEventLogs />}
            />
            <Route
              path="cost-event-logs/:logId"
              element={<CostEventLogDetail />}
            />
            {/* Partner Billing Log — partner-facing INR billing rows. */}
            <Route
              path="partner-billing-logs"
              element={<PartnerBillingLogs />}
            />
            <Route
              path="partner-billing-logs/:logId"
              element={<PartnerBillingLogDetail />}
            />
            {/* R26 — OTP admin surface + detail. */}
            <Route path="otps" element={<Otps />} />
            <Route path="otps/:otpId" element={<OtpDetail />} />
            {/* GST Auth Sessions — per-GSTIN session lifecycle log.
                Tokens MASKED by default; raw tokens revealed only via
                explicit eye-icon click → audit-logged reveal endpoint. */}
            <Route
              path="gst-auth-sessions"
              element={<AdminGstAuthSessions />}
            />
            <Route
              path="gst-auth-sessions/:sessionId"
              element={<AdminGstAuthSessionDetail />}
            />
            {/* Email logs admin surface + detail. */}
            <Route path="email-logs" element={<EmailLogs />} />
            <Route path="email-logs/:logId" element={<EmailLogDetail />} />
            {/* R27 — Generate response log admin surface + detail. */}
            <Route
              path="generate-response-logs"
              element={<GenerateResponseLogs />}
            />
            <Route
              path="generate-response-logs/:logId"
              element={<GenerateResponseLogDetail />}
            />
            {/* R29 — Comprehensive analysis query log admin surface + detail. */}
            <Route
              path="comprehensive-analysis-query-logs"
              element={<ComprehensiveAnalysisQueryLogs />}
            />
            <Route
              path="comprehensive-analysis-query-logs/:logId"
              element={<ComprehensiveAnalysisQueryLogDetail />}
            />
            {/* Import Log — admin surface for `data_imports` Excel bulk-import history. */}
            <Route path="import-logs" element={<ImportLogs />} />
            <Route path="import-logs/:importId" element={<ImportLogDetail />} />
            {/* Login Failed Log — admin surface for `login_failed` portal login-failure tracking. */}
            <Route path="login-failed-logs" element={<LoginFailedLogs />} />
            <Route path="login-failed-logs/:logId" element={<LoginFailedLogDetail />} />

            {/* Service-table sidebar groups (GST / Income Tax / TDS-TCS).
                Phase 1 — placeholder shells; per-page columns + filters
                land once the field spec is provided. */}
            <Route
              path="gst/clients"
              element={
                <AdminClientsPage
                  title="GST Clients"
                  description="All GST clients across the platform."
                  telemetryKey="admin.gst.clients"
                  listEndpoint={ADMIN.SERVICE_GST_CLIENTS}
                  partnerRefsEndpoint={ADMIN.SERVICE_GST_CLIENT_PARTNER_REFS}
                  assigneesEndpoint={ADMIN.SERVICE_GST_CLIENT_ASSIGNEES}
                  deletePath="/api/v1/admin/gst/clients"
                  detailBasePath="/admin/gst/clients"
                  columns={GST_CLIENTS_COLUMNS}
                  searchPlaceholder="Search by client name, GSTIN, or GST username"
                  emptyText="No GST clients match your filters."
                  xScroll={1600}
                />
              }
            />
            <Route
              path="gst/notices"
              element={
                <AdminNoticesPage
                  title="GST Notices & Orders"
                  description="GST notices and orders raised across the platform."
                  telemetryKey="admin.gst.notices"
                  listEndpoint={ADMIN.SERVICE_GST_NOTICES}
                  clientNamesEndpoint={ADMIN.SERVICE_GST_NOTICES_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_GST_NOTICES_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_GST_NOTICES_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/gst/notices"
                  detailBasePath="/admin/gst/notices"
                  columns={GST_NOTICES_COLUMNS}
                  emptyText="No GST notices match your filters."
                  xScroll={1500}
                />
              }
            />
            <Route
              path="gst/additional-notices"
              element={
                <AdminNoticesPage
                  title="GST Additional Notices"
                  description="GST additional notices issued across the platform."
                  telemetryKey="admin.gst.additional-notices"
                  listEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES}
                  clientNamesEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_GST_ADDITIONAL_NOTICES_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/gst/additional-notices"
                  detailBasePath="/admin/gst/additional-notices"
                  columns={GST_ADDITIONAL_NOTICES_COLUMNS}
                  emptyText="No GST additional notices match your filters."
                  xScroll={1500}
                />
              }
            />
            <Route
              path="income-tax/clients"
              element={
                <AdminClientsPage
                  title="Income Tax Clients"
                  description="All Income Tax clients across the platform."
                  telemetryKey="admin.income-tax.clients"
                  listEndpoint={ADMIN.SERVICE_IT_CLIENTS}
                  partnerRefsEndpoint={ADMIN.SERVICE_IT_CLIENT_PARTNER_REFS}
                  assigneesEndpoint={ADMIN.SERVICE_IT_CLIENT_ASSIGNEES}
                  deletePath="/api/v1/admin/income-tax/clients"
                  detailBasePath="/admin/income-tax/clients"
                  columns={IT_CLIENTS_COLUMNS}
                  searchPlaceholder="Search by client name or PAN/TAN"
                  emptyText="No Income Tax clients match your filters."
                  xScroll={1600}
                />
              }
            />
            <Route
              path="income-tax/eproceedings"
              element={
                <AdminItNoticesPage
                  title="Income Tax E-Proceedings"
                  description="E-Proceeding records across the platform."
                  telemetryKey="admin.income-tax.eproceedings"
                  listEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS}
                  clientNamesEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_IT_EPROCEEDINGS_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/income-tax/eproceedings"
                  detailBasePath="/admin/income-tax/eproceedings"
                  columns={IT_EPROCEEDINGS_COLUMNS}
                  emptyText="No E-Proceedings match your filters."
                  xScroll={1500}
                />
              }
            />
            <Route
              path="income-tax/response-outstanding-demands"
              element={
                <AdminItNoticesPage
                  title="Income Tax - Response to Outstanding Demands"
                  description="Responses raised against outstanding demands."
                  telemetryKey="admin.income-tax.response-outstanding-demands"
                  listEndpoint={ADMIN.SERVICE_IT_ROD}
                  clientNamesEndpoint={ADMIN.SERVICE_IT_ROD_CLIENT_NAMES}
                  typesEndpoint={ADMIN.SERVICE_IT_ROD_TYPES}
                  responseStatusesEndpoint={ADMIN.SERVICE_IT_ROD_RESPONSE_STATUSES}
                  deletePath="/api/v1/admin/income-tax/response-outstanding-demands"
                  detailBasePath="/admin/income-tax/response-outstanding-demands"
                  columns={IT_ROD_COLUMNS}
                  emptyText="No outstanding-demand responses match your filters."
                  xScroll={1500}
                />
              }
            />
            <Route
              path="tds-tcs/clients"
              element={
                <AdminClientsPage
                  title="TDS / TCS Clients"
                  description="All TDS / TCS clients across the platform."
                  telemetryKey="admin.tds-tcs.clients"
                  listEndpoint={ADMIN.SERVICE_TDS_CLIENTS}
                  partnerRefsEndpoint={ADMIN.SERVICE_TDS_CLIENT_PARTNER_REFS}
                  assigneesEndpoint={ADMIN.SERVICE_TDS_CLIENT_ASSIGNEES}
                  deletePath="/api/v1/admin/tds-tcs/clients"
                  detailBasePath="/admin/tds-tcs/clients"
                  columns={TDS_CLIENTS_COLUMNS}
                  searchPlaceholder="Search by client name, TAN, or TDS username"
                  emptyText="No TDS / TCS clients match your filters."
                  xScroll={1700}
                />
              }
            />
            <Route
              path="tds-tcs/notices"
              element={
                <AdminTdsNoticesPage
                  deletePath="/api/v1/admin/tds-tcs/notices"
                  detailBasePath="/admin/tds-tcs/notices"
                />
              }
            />

            {/* R10 — record detail routes. One generic AdminRecordDetailPage
                + 8 prop sets (3 client + 5 notice). */}
            <Route
              path="gst/clients/:id"
              element={
                <AdminRecordDetailPage
                  {...GST_CLIENT_DETAIL}
                  backLink="/admin/gst/clients"
                  endpoint={(id) => `/api/v1/admin/gst/clients/${id}`}
                  deletePath="/api/v1/admin/gst/clients"
                  deleteAllNoticesPortal="gst"
                  telemetryKey="admin.gst.clients.detail"
                />
              }
            />
            <Route
              path="income-tax/clients/:id"
              element={
                <AdminRecordDetailPage
                  {...IT_CLIENT_DETAIL}
                  backLink="/admin/income-tax/clients"
                  endpoint={(id) =>
                    `/api/v1/admin/income-tax/clients/${id}`
                  }
                  deletePath="/api/v1/admin/income-tax/clients"
                  telemetryKey="admin.income-tax.clients.detail"
                />
              }
            />
            <Route
              path="tds-tcs/clients/:id"
              element={
                <AdminRecordDetailPage
                  {...TDS_CLIENT_DETAIL}
                  backLink="/admin/tds-tcs/clients"
                  endpoint={(id) => `/api/v1/admin/tds-tcs/clients/${id}`}
                  deletePath="/api/v1/admin/tds-tcs/clients"
                  telemetryKey="admin.tds-tcs.clients.detail"
                />
              }
            />
            <Route
              path="gst/notices/:id"
              element={
                <AdminRecordDetailPage
                  {...GST_NOTICE_DETAIL}
                  backLink="/admin/gst/notices"
                  endpoint={(id) => `/api/v1/admin/gst/notices/${id}`}
                  deletePath="/api/v1/admin/gst/notices"
                  telemetryKey="admin.gst.notices.detail"
                  tabs={[
                    { key: "overview", label: "Overview" },
                    {
                      key: "notice-details",
                      label: "Notice Details",
                      content: (record) => (
                        <AdminNoticeReadOnlyForm
                          record={record}
                          surface="gst-notice"
                        />
                      ),
                    },
                    {
                      key: "summary",
                      label: "Summary Details",
                      content: (record) => (
                        <AdminNoticeSummaryView record={record} />
                      ),
                    },
                  ]}
                />
              }
            />
            <Route
              path="gst/additional-notices/:id"
              element={
                <AdminRecordDetailPage
                  {...GST_ADDITIONAL_NOTICE_DETAIL}
                  backLink="/admin/gst/additional-notices"
                  endpoint={(id) =>
                    `/api/v1/admin/gst/additional-notices/${id}`
                  }
                  deletePath="/api/v1/admin/gst/additional-notices"
                  telemetryKey="admin.gst.additional-notices.detail"
                  tabs={[
                    { key: "overview", label: "Overview" },
                    {
                      key: "notice-details",
                      label: "Notice Details",
                      content: (record) => (
                        <AdminNoticeReadOnlyForm
                          record={record}
                          surface="gst-additional-notice"
                        />
                      ),
                    },
                    {
                      key: "summary",
                      label: "Summary Details",
                      content: (record) => (
                        <AdminNoticeSummaryView record={record} />
                      ),
                    },
                  ]}
                />
              }
            />
            <Route
              path="income-tax/eproceedings/:id"
              element={
                <AdminRecordDetailPage
                  {...IT_EPROCEEDING_DETAIL}
                  backLink="/admin/income-tax/eproceedings"
                  endpoint={(id) =>
                    `/api/v1/admin/income-tax/eproceedings/${id}`
                  }
                  deletePath="/api/v1/admin/income-tax/eproceedings"
                  telemetryKey="admin.income-tax.eproceedings.detail"
                  tabs={[
                    { key: "overview", label: "Overview" },
                    {
                      key: "notice-details",
                      label: "Notice Details",
                      content: (record) => (
                        <AdminNoticeReadOnlyForm
                          record={record}
                          surface="it-eproceeding"
                        />
                      ),
                    },
                    {
                      key: "summary",
                      label: "Summary Details",
                      content: (record) => (
                        <AdminNoticeSummaryView record={record} />
                      ),
                    },
                  ]}
                />
              }
            />
            <Route
              path="income-tax/response-outstanding-demands/:id"
              element={
                <AdminRecordDetailPage
                  {...IT_ROD_DETAIL}
                  backLink="/admin/income-tax/response-outstanding-demands"
                  endpoint={(id) =>
                    `/api/v1/admin/income-tax/response-outstanding-demands/${id}`
                  }
                  deletePath="/api/v1/admin/income-tax/response-outstanding-demands"
                  telemetryKey="admin.income-tax.response-outstanding-demands.detail"
                  tabs={[
                    { key: "overview", label: "Overview" },
                    {
                      key: "notice-details",
                      label: "Notice Details",
                      content: (record) => (
                        <AdminNoticeReadOnlyForm
                          record={record}
                          surface="it-rod"
                        />
                      ),
                    },
                    {
                      key: "summary",
                      label: "Summary Details",
                      content: (record) => (
                        <AdminNoticeSummaryView record={record} />
                      ),
                    },
                  ]}
                />
              }
            />
            <Route
              path="tds-tcs/notices/:id"
              element={
                <AdminRecordDetailPage
                  {...TDS_NOTICE_DETAIL}
                  backLink="/admin/tds-tcs/notices"
                  endpoint={(id) => `/api/v1/admin/tds-tcs/notices/${id}`}
                  deletePath="/api/v1/admin/tds-tcs/notices"
                  telemetryKey="admin.tds-tcs.notices.detail"
                />
              }
            />
          </Route>
        </Route>

        {/* The Partner Admin portal moved OUT of this app into the standalone
            NoticeAI Partner Portal, which merges it with the Partner Sandbox so
            a partner manages its Live and Sandbox environments in one place
            (PARTNER_PORTAL_MERGE_PLAN.md). Every /partner-admin/* BACKEND
            endpoint stays: the new portal is a client of them. The pages that
            used to live here are in this repo's history at 9353ba6. */}

        {/* Module 11 — Iframe embed surface. Auth handled via in-memory
            token from EmbedTokenProvider inside EmbedLayout (NOT
            localStorage). The four allowed pages are listed explicitly
            here; any other /embed/* path falls into EmbedLayout's
            forbidden-prefix branch which renders a "not available"
            alert. Belt-and-braces with the BE iframe-security
            middleware (which 404s /embed/admin* + /embed/partner-admin*
            server-side). */}
        <Route path="/embed" element={<EmbedLayout />}>
          {/* Iframe Reuse Refactor v3.5 — mount real tenant pages
              under `/embed/*`. EmbedLayout wraps the Outlet in
              `EmbedModeContext({ isEmbed: true, readOnly: true })` so
              every tenant page below reads `useEmbedMode().isEmbed`
              and suppresses chrome (Navbar/Footer in the portal
              layouts; upgrade modals + hero in Dashboard).

              Smoke triage (28-04-2026 v3.5):
                - Eproceedings, GstNoticesList, TDSNoticesList all
                  PASS org-wide (`:clientId` is optional, FE filters
                  push only when present, BE endpoints accept missing
                  `client=`). EmbedNotices.jsx is therefore deleted —
                  the bespoke list page is now redundant.
                - EmbedNoticeDetail.jsx is RETAINED per spec §3.3
                  read-mostly principle: EPForm.jsx (the tenant
                  detail+editor) is a 5K-line write surface, too risky
                  to expose in an iframe surface. Wholesale parity
                  here would require a separate audit. */}
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />

          {/* GST tenant tree mirrored under /embed/gstin/* */}
          <Route path={ServiceType.GSTIN} element={<GSTLayout />}>
            <Route index element={<Gstin serviceType={ServiceType.GSTIN} />} />
            <Route
              path="clients"
              element={<Clients serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="client-view/:clientId"
              element={<ClientView serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="clients/:clientId"
              element={<ClientView serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="eproceedings/:clientId"
              element={<Eproceedings serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="notices"
              element={<GSTNoticeList serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="notices/:clientId"
              element={<GSTNoticeList serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="add-notices/:clientId"
              element={<AddGSTNoticeForm serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="add-additional-notices/:clientId"
              element={<AddAdditionalNoticeForm serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="additional-notices/:clientId"
              element={<GSTAdditionalNoticesList serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="eproceeding-details"
              element={<EPForm serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="failed-login-details"
              element={<EPForm serviceType={ServiceType.GSTIN} />}
            />
            <Route
              path="notice-details"
              element={<EPForm serviceType={ServiceType.GSTIN} />}
            />
          </Route>

          {/* Income Tax tenant tree mirrored under /embed/income-tax/* */}
          <Route path={ServiceType.INCOME_TAX} element={<IncomeTaxLayout />}>
            <Route
              index
              element={<IncomeTax serviceType={ServiceType.INCOME_TAX} />}
            />
            <Route
              path="notice"
              element={<Notice serviceType={ServiceType.INCOME_TAX} />}
            />
            <Route
              path="clients"
              element={<Clients serviceType={ServiceType.INCOME_TAX} />}
            />
            <Route
              path="client-view/:clientId"
              element={<ClientView serviceType={ServiceType.INCOME_TAX} />}
            />
            <Route
              path="eproceedings/:clientId"
              element={<Eproceedings serviceType={ServiceType.INCOME_TAX} />}
            />
            <Route
              path="notices"
              element={<Eproceedings serviceType={ServiceType.INCOME_TAX} />}
            />
            <Route
              path="responseoutstandings/:clientId"
              element={
                <ResponseOutstandings serviceType={ServiceType.INCOME_TAX} />
              }
            />
            <Route
              path="add-eproceedings/:clientId"
              element={<AddEproceedingForm serviceType={ServiceType.INCOME_TAX} />}
            />
            <Route
              path="add-responseoutstandings/:clientId"
              element={
                <AddResponseOutstandingsForm serviceType={ServiceType.INCOME_TAX} />
              }
            />
            <Route
              path="eproceeding-details"
              element={<EPForm serviceType={ServiceType.INCOME_TAX} />}
            />
            <Route
              path="failed-login-details"
              element={<EPForm serviceType={ServiceType.INCOME_TAX} />}
            />
            <Route
              path="responseoutstandings-details"
              element={<EPForm serviceType={ServiceType.INCOME_TAX} />}
            />
          </Route>

          {/* TDS tenant tree mirrored under /embed/tds/* */}
          <Route path={ServiceType.TDS} element={<TDSLayout />}>
            <Route index element={<Tds serviceType={ServiceType.TDS} />} />
            <Route
              path="clients"
              element={<Clients serviceType={ServiceType.TDS} />}
            />
            <Route
              path="client-view/:clientId"
              element={<ClientView serviceType={ServiceType.TDS} />}
            />
            <Route
              path="eproceedings/:clientId"
              element={<Eproceedings serviceType={ServiceType.TDS} />}
            />
            <Route
              path="notices"
              element={<TDSNoticeList serviceType={ServiceType.TDS} />}
            />
            <Route
              path="notices/:clientId"
              element={<TDSNoticeList serviceType={ServiceType.TDS} />}
            />
            <Route
              path="add-notices/:clientId"
              element={<ADDTDSNoticeForm serviceType={ServiceType.TDS} />}
            />
            <Route
              path="eproceeding-details"
              element={<EPForm serviceType={ServiceType.TDS} />}
            />
            <Route
              path="failed-login-details"
              element={<EPForm serviceType={ServiceType.TDS} />}
            />
            <Route
              path="notice-details"
              element={<EPForm serviceType={ServiceType.TDS} />}
            />
          </Route>

          {/* Notice detail surface kept as bespoke Embed* page (see
              note above re: EPForm size). The `:portal` segment maps
              `income-tax | gst | tds` per the spec — distinct from
              the tenant route shape (which uses `gstin`). */}
          <Route
            path="notices/:portal/:noticeId"
            element={<EmbedNoticeDetail />}
          />
          {/* AI response generation flow (Module 11 §4.2 line 72).
              Path matches the spec verbatim — `/embed/generate-response/{id}`.
              Optional `?portal=<seg>` defaults to income-tax (the only
              portal with mature with_legal=true support today). GST/TDS
              render an "open in tenant app" empty state. */}
          <Route
            path="generate-response/:noticeId"
            element={<EmbedGenerateResponse />}
          />

          {/* Team management mirror of the non-embed /team mount
              (App.js:113). Admin-gated by <TenantAdminEmbedRoute />,
              which reads role from the SSO JWT claims (AuthContext is
              empty inside the iframe). The page itself is the same
              <TeamPage /> the non-embed surface uses; it falls back
              to claims.sub / claims.role for self-row protection when
              rendered under EmbedLayout. */}
          <Route element={<TenantAdminEmbedRoute />}>
            <Route path="team" element={<TeamPage />} />
          </Route>
          {/* Catch-all inside /embed: EmbedLayout's pre-render switch
              already handles forbidden prefixes (/embed/admin*,
              /embed/partner-admin*, /embed/settings*) with a "not
              available" alert. Anything else (typos, dropped routes)
              renders an embed-styled not-found shell so we never fall
              through to the global Error404 page (which would paint
              the regular tenant chrome — wrong context inside an
              iframe). */}
          <Route path="*" element={<EmbedNotFound />} />
        </Route>

        <Route path="*" element={<Error404 />} />
      </Routes>
      </Suspense>
    </>
  );
}

export default App;
